import React, { type ComponentProps } from 'react'
import { css, cx } from '@linaria/core'
import PropTypes from 'prop-types'
import { skeletonCss } from './BaseSkeleton'
import * as Type from '../typography'

const randomWidths = [0.75, 0.95, 1, 0.8, 0.7, 0.9, 0.85, 0.92, 0.7, 0.77]

function getWidth(seed: number): number {
  return randomWidths[seed % randomWidths.length]
}

const baseTypeSkeletonCss = cx(
  skeletonCss,
  css`
    border-radius: 1000000px;
    display: inline-block;
    min-height: 0.7em;
  `
)

type TypeSkeletonProps = {
  /** Number of lines of skeleton "text" to display. */
  lineCount?: number

  /** Max width the skeleton should extend to. Useful for limiting skeletons to general sizes. */
  maxWidth?: number | string

  /** A random seed to vary the widths of multiple TypeSkeletons used together. */
  randomSeed?: number
}

export function TypeSkeleton({ lineCount = 1, maxWidth = '100%', randomSeed = 0 }: TypeSkeletonProps): JSX.Element {
  const width = typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth
  return (
    <>
      {Array.from({ length: lineCount }, (_, index) => (
        <span
          className={baseTypeSkeletonCss}
          key={index}
          style={{
            width: `calc(${width}*${getWidth(index + randomSeed)})`,
            maxWidth: `min(100%, ${width})`,
          }}
        />
      ))}
    </>
  )
}

// this is used in some instances (like InputBase) to allow for a skeleton to be passed in instead of actual text
TypeSkeleton.identity = 'Skeleton'

TypeSkeleton.propTypes = {
  /** Number of lines of skeleton "text" to display. */
  lineCount: PropTypes.number,

  /** Max width the skeleton should extend to. Useful for limiting skeletons to general sizes. */
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}

type HeadingSkeletonProps = {
  /** Number of lines the main heading has. */
  lineCount?: number

  /** Whether to show an overline skeleton. */
  hasOverline?: boolean

  /** Number of lines the Subheading skeleton has. */
  subheadingLineCount?: number

  /** Max width the overline skeleton should extend to. */
  maxWidth?: number | string
} & Omit<ComponentProps<typeof Type.GenericHeading>, 'size' | 'aria-hidden' | 'as'>

function makeHeadingSkeleton(size: ComponentProps<typeof Type.GenericHeading>['size']) {
  return function HeadingSkeleton({
    lineCount = 1,
    hasOverline = false,
    subheadingLineCount = 0,
    maxWidth = '50%',
    ...props
  }: HeadingSkeletonProps): JSX.Element {
    return (
      <Type.GenericHeading {...props} aria-hidden as="span" size={size}>
        {hasOverline && (
          <Type.Overline>
            <TypeSkeleton maxWidth={maxWidth} />
          </Type.Overline>
        )}
        <TypeSkeleton lineCount={lineCount} />
        {subheadingLineCount > 0 && (
          <Type.Subheading>
            <TypeSkeleton lineCount={subheadingLineCount} randomSeed={1} />
          </Type.Subheading>
        )}
      </Type.GenericHeading>
    )
  }
}

export const H1 = makeHeadingSkeleton('H1')
export const H2 = makeHeadingSkeleton('H2')
export const H3 = makeHeadingSkeleton('H3')
export const H4 = makeHeadingSkeleton('H4')
export const H5 = makeHeadingSkeleton('H5')
export const H6 = makeHeadingSkeleton('H6')

type ParagraphSkeletonProps = Pick<ComponentProps<typeof TypeSkeleton>, 'lineCount' | 'maxWidth'> &
  Omit<ComponentProps<typeof Type.Paragraph>, 'as'>

export const Paragraph = ({ lineCount, maxWidth, ...rest }: ParagraphSkeletonProps): JSX.Element => (
  <Type.Paragraph {...rest} as="span">
    <TypeSkeleton lineCount={lineCount} maxWidth={maxWidth} />
  </Type.Paragraph>
)
